import React, { useCallback, useMemo, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Grid, Chip } from '@material-ui/core'
import SydLabel from '../../../../../../../commonDesign/SydLabel'
import SydSelect from '../../../../../../../commonDesign/SydSelect'
import SydInput from '../../../../../../../commonDesign/SydInput'
import SydButton from '../../../../../../../commonDesign/Button'
import { formatDisplayName } from '../../../../../../../../utils/formatters'
import ColumnConfigEditor, { DATA_TYPE_TO_SORT_AS } from './ColumnConfigEditor'
import StyleVariantPicker from './StyleVariantPicker'
import LocalStorageAndFeatureToggles from './LocalStorageAndFeatureToggles'
import ExportSettingsEditor from './ExportSettingsEditor'
import DefaultSortEditor from './DefaultSortEditor'
import FooterMappingEditor from './FooterMappingEditor'

function PresentationTableConfigEditor ({ config, allPurposes, onUpdate }) {
  const [localConfig, setLocalConfig] = useState(config)
  const [newVariationGroup, setNewVariationGroup] = useState('')
  const [showVariationInput, setShowVariationInput] = useState(false)

  useEffect(() => {
    if (JSON.stringify(localConfig) !== JSON.stringify(config)) {
      onUpdate(localConfig)
    }
  }, [localConfig, config, onUpdate])

  const purposeOptions = useMemo(() => {
    const purposes = Object.entries(allPurposes || {})
    return [
      { label: 'Select a purpose...', value: '__placeholder__' },
      ...purposes.map(([code, data]) => ({
        label: `${data.displayName} (${code})`,
        value: code
      }))
    ]
  }, [allPurposes])

  const footerPurposeOptions = useMemo(() => {
    const purposes = Object.entries(allPurposes || {})
    return [
      { label: 'None', value: '__none__' },
      ...purposes.map(([code, data]) => ({
        label: `${data.displayName} (${code})`,
        value: code
      }))
    ]
  }, [allPurposes])

  const selectedPurposeColumns = useMemo(() => {
    if (!localConfig.purposeCode || !allPurposes[localConfig.purposeCode]) {
      return []
    }
    return allPurposes[localConfig.purposeCode].columns || []
  }, [localConfig.purposeCode, allPurposes])

  const footerPurposeColumns = useMemo(() => {
    if (!localConfig.footerPurposeCode || !allPurposes[localConfig.footerPurposeCode]) {
      return []
    }
    return allPurposes[localConfig.footerPurposeCode].columns || []
  }, [localConfig.footerPurposeCode, allPurposes])

  const handlePurposeChange = useCallback((purposeCode) => {
    const purpose = allPurposes[purposeCode]
    if (purpose && purpose.columns) {
      const prefillColumns = purpose.columns.map(col => ({
        name: col.name,
        displayName: formatDisplayName(col.name),
        description: col.description || null,
        formatString: null,
        align: ['integer', 'decimal'].includes(col.dataType) ? 'right' : 'left',
        sortable: col.sortable !== false,
        filterable: col.filterable !== false,
        hidden: col.hidden || false,
        exclude: false,
        width: 150,
        sortAs: DATA_TYPE_TO_SORT_AS[col.dataType] || null
      }))
      setLocalConfig(prev => ({
        ...prev,
        purposeCode,
        columns: prefillColumns
      }))
    } else {
      setLocalConfig(prev => ({
        ...prev,
        purposeCode: purposeCode === '__placeholder__' ? null : (purposeCode || null),
        columns: []
      }))
    }
  }, [allPurposes])

  const handleAddColumn = useCallback((column) => {
    setLocalConfig(prev => ({
      ...prev,
      columns: [...prev.columns, column]
    }))
  }, [])

  const handleRemoveColumn = useCallback((index) => {
    setLocalConfig(prev => ({
      ...prev,
      columns: prev.columns.filter((_, i) => i !== index)
    }))
  }, [])

  const handleUpdateColumn = useCallback((index, updated) => {
    setLocalConfig(prev => ({
      ...prev,
      columns: prev.columns.map((col, i) => i === index ? updated : col)
    }))
  }, [])

  const handleColumnsChange = useCallback((newColumns) => {
    setLocalConfig(prev => ({
      ...prev,
      columns: newColumns
    }))
  }, [])

  const handleAddSortField = useCallback(() => {
    setLocalConfig(prev => ({
      ...prev,
      defaultSort: [...(prev.defaultSort || []), { field: '', dir: 'asc' }]
    }))
  }, [])

  const handleRemoveSortField = useCallback((index) => {
    setLocalConfig(prev => ({
      ...prev,
      defaultSort: prev.defaultSort.filter((_, i) => i !== index)
    }))
  }, [])

  const handleUpdateSortField = useCallback((index, field, value) => {
    setLocalConfig(prev => ({
      ...prev,
      defaultSort: prev.defaultSort.map((sort, i) =>
        i === index ? { ...sort, [field]: value } : sort
      )
    }))
  }, [])

  const sortFieldOptions = useMemo(() => {
    return localConfig.columns.map(col => ({
      label: col.displayName || col.name,
      value: col.name
    }))
  }, [localConfig.columns])

  const handleAddVariationGroup = useCallback(() => {
    const trimmed = newVariationGroup.trim().toLowerCase().replace(/\s+/g, '-')
    if (!trimmed) return
    if ((localConfig.variationGroups || []).includes(trimmed)) {
      setNewVariationGroup('')
      setShowVariationInput(false)
      return
    }
    setLocalConfig(prev => ({
      ...prev,
      variationGroups: [...(prev.variationGroups || []), trimmed]
    }))
    setNewVariationGroup('')
    setShowVariationInput(false)
  }, [newVariationGroup, localConfig.variationGroups])

  const handleRemoveVariationGroup = useCallback((groupToRemove) => {
    setLocalConfig(prev => ({
      ...prev,
      variationGroups: (prev.variationGroups || []).filter(g => g !== groupToRemove)
    }))
  }, [])

  const handleFooterPurposeChange = useCallback((footerPurposeCode) => {
    setLocalConfig(prev => ({
      ...prev,
      footerPurposeCode: footerPurposeCode === '__none__' ? null : (footerPurposeCode || null),
      footerColumnMapping: [] // Clear mappings when changing footer purpose
    }))
  }, [])

  // Get the footer column mapped to a main column (if any)
  const getFooterColumnForMain = useCallback((mainColumnName) => {
    const mapping = (localConfig.footerColumnMapping || []).find(m => m.mainColumn === mainColumnName)
    return mapping?.footerColumn || '__none__'
  }, [localConfig.footerColumnMapping])

  // Update the footer column for a specific main column
  const handleFooterMappingChange = useCallback((mainColumnName, footerColumnName) => {
    setLocalConfig(prev => {
      // Remove any existing mapping for this main column
      let newMappings = (prev.footerColumnMapping || []).filter(m => m.mainColumn !== mainColumnName)

      // Add new mapping if a footer column was selected
      if (footerColumnName && footerColumnName !== '__none__') {
        newMappings = [...newMappings, { mainColumn: mainColumnName, footerColumn: footerColumnName }]
      }

      return {
        ...prev,
        footerColumnMapping: newMappings
      }
    })
  }, [])

  // Auto-configure: match columns by name
  const handleAutoConfigureFooterMapping = useCallback(() => {
    const footerColumnNames = new Set(footerPurposeColumns.map(c => c.name))
    const newMappings = (localConfig.columns || [])
      .filter(col => footerColumnNames.has(col.name))
      .map(col => ({ mainColumn: col.name, footerColumn: col.name }))

    setLocalConfig(prev => ({
      ...prev,
      footerColumnMapping: newMappings
    }))
  }, [localConfig.columns, footerPurposeColumns])

  // Clear all footer mappings
  const handleClearFooterMappings = useCallback(() => {
    setLocalConfig(prev => ({
      ...prev,
      footerColumnMapping: []
    }))
  }, [])

  const footerColumnSelectOptions = useMemo(() => {
    return [
      { label: '(None)', value: '__none__' },
      ...footerPurposeColumns.map(col => ({
        label: col.name,
        value: col.name
      }))
    ]
  }, [footerPurposeColumns])

  // Count how many columns have mappings
  const mappedColumnCount = useMemo(() => {
    return (localConfig.footerColumnMapping || []).length
  }, [localConfig.footerColumnMapping])

  return (
    <Grid container spacing={2}>
      {/* Data Purpose Selection */}
      <Grid item xs={12}>
        <SydLabel required label='Data Configuration Purpose' helperText='Select which data purpose to display'>
          <SydSelect
            options={purposeOptions}
            value={localConfig.purposeCode || '__placeholder__'}
            onChange={(e) => handlePurposeChange(e.target.value)}
          />
        </SydLabel>
      </Grid>

      {/* Latch Date Mode - only show when selected purpose has latch config */}
      {localConfig.purposeCode && allPurposes[localConfig.purposeCode]?.latch
        ? (
          <Grid item xs={12}>
            <SydLabel label='Latch Date Mode' helperText='How end users interact with the snapshot date'>
              <SydSelect
                options={[
                  { label: 'User Selectable', value: 'selectable' },
                  { label: 'Latest Only (read-only)', value: 'latest' }
                ]}
                value={localConfig.latchMode || 'selectable'}
                onChange={(e) => setLocalConfig(prev => ({ ...prev, latchMode: e.target.value }))}
              />
            </SydLabel>
          </Grid>
        )
        : null}

      {/* Column Configuration */}
      <Grid item xs={12}>
        <ColumnConfigEditor
          columns={localConfig.columns || []}
          availableColumns={selectedPurposeColumns}
          onAddColumn={handleAddColumn}
          onRemoveColumn={handleRemoveColumn}
          onUpdateColumn={handleUpdateColumn}
          onColumnsChange={handleColumnsChange}
          showSortAs
        />
      </Grid>

      {/* Footer Purpose (Optional) */}
      <Grid item xs={12}>
        <SydLabel label='Footer Data Purpose' helperText='Optional purpose for footer/summary data'>
          <SydSelect
            options={footerPurposeOptions}
            value={localConfig.footerPurposeCode || '__none__'}
            onChange={(e) => handleFooterPurposeChange(e.target.value)}
          />
        </SydLabel>
      </Grid>

      {/* Footer Column Mapping - only show when footer purpose is selected */}
      {localConfig.footerPurposeCode
        ? (
          <FooterMappingEditor
            localConfig={localConfig}
            getFooterColumnForMain={getFooterColumnForMain}
            onFooterMappingChange={handleFooterMappingChange}
            footerColumnSelectOptions={footerColumnSelectOptions}
            mappedColumnCount={mappedColumnCount}
            onAutoConfigure={handleAutoConfigureFooterMapping}
            onClear={handleClearFooterMappings}
          />
        )
        : null}

      {/* Default Sort */}
      <DefaultSortEditor
        localConfig={localConfig}
        setLocalConfig={setLocalConfig}
        sortFieldOptions={sortFieldOptions}
        onAddSort={handleAddSortField}
        onRemoveSort={handleRemoveSortField}
        onUpdateSort={handleUpdateSortField}
      />

      {/* Top N Rows */}
      <Grid item xs={12} sm={6} md={3}>
        <SydLabel label='Top N Rows' helperText='Limit the table to the first N rows based on the query sort order. Leave empty to show all rows (up to 1000).'>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <SydInput
              type='number'
              value={localConfig.topN ?? ''}
              onChange={(e) => {
                const raw = e.target.value
                setLocalConfig(prev => ({
                  ...prev,
                  topN: raw === '' ? null : Math.max(1, Math.min(1000, parseInt(raw, 10) || 1))
                }))
              }}
              placeholder='All rows'
              style={{ maxWidth: '160px' }}
            />
            {localConfig.topN != null
              ? (
                <SydButton
                  size='sm'
                  variant='ghost'
                  onClick={() => setLocalConfig(prev => ({ ...prev, topN: null }))}
                >
                  Clear
                </SydButton>
              )
              : null}
          </div>
        </SydLabel>
      </Grid>

      {/* Variation Groups */}
      <Grid item xs={12}>
        <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <h4 style={{ margin: 0 }}>Variation Groups</h4>
          {!showVariationInput
            ? (
              <SydButton
                size='sm'
                icon='add'
                onClick={() => setShowVariationInput(true)}
              >
                Add Group
              </SydButton>
            )
            : null}
        </div>

        {showVariationInput
          ? (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px' }}>
              <SydInput
                value={newVariationGroup}
                onChange={(e) => setNewVariationGroup(e.target.value)}
                placeholder='Enter group name'
                style={{ maxWidth: '200px' }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleAddVariationGroup()
                  }
                  if (e.key === 'Escape') {
                    setShowVariationInput(false)
                    setNewVariationGroup('')
                  }
                }}
                autoFocus
              />
              <SydButton size='sm' variant='primary' onClick={handleAddVariationGroup} disabled={!newVariationGroup.trim()}>
                Add
              </SydButton>
              <SydButton size='sm' variant='ghost' onClick={() => { setShowVariationInput(false); setNewVariationGroup('') }}>
                Cancel
              </SydButton>
            </div>
          )
          : null}

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {(localConfig.variationGroups || []).map((group) => (
            <Chip
              key={group}
              label={group}
              onDelete={() => handleRemoveVariationGroup(group)}
              variant='outlined'
              size='small'
            />
          ))}
          {(localConfig.variationGroups || []).length === 0
            ? (
              <span style={{ color: 'var(--text-color-subtle)', fontSize: '0.875rem' }}>
                No variation groups configured
              </span>
            )
            : null}
        </div>
      </Grid>

      <LocalStorageAndFeatureToggles localConfig={localConfig} setLocalConfig={setLocalConfig} />

      <ExportSettingsEditor localConfig={localConfig} setLocalConfig={setLocalConfig} />

      <StyleVariantPicker
        presentationType='Presentation Table'
        value={localConfig.styleVariant}
        onChange={(v) => setLocalConfig(prev => ({ ...prev, styleVariant: v, styleVariantParams: {} }))}
        variantParams={localConfig.styleVariantParams}
        onParamsChange={(p) => setLocalConfig(prev => ({ ...prev, styleVariantParams: p }))}
      />
    </Grid>
  )
}

PresentationTableConfigEditor.propTypes = {
  config: PropTypes.object.isRequired,
  allPurposes: PropTypes.object.isRequired,
  onUpdate: PropTypes.func.isRequired
}

export default PresentationTableConfigEditor
